/* eslint-disable complexity */
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER, FLEX_CONTAINER_CENTERED, FLEX_CONTAINER_COLUMN, FLEX_WRAP, H_100, H_50, POSITION, TEXT_ALIGN, W_100, W_50 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CssStyleFields, KeyboardInput, REF_ATTRIBUTE, createHtmlElement, empty, findElementByRef, setHTMLElementStyle, setHtmlElementOptions } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { isNumber, toPx } from "@cervval.js/common/src/tools/number.tools";
import { MONTHS_IN_ONE_YEAR } from "@cervval.js/common/src/tools/time.tools";
import { compareAsc } from "@cervval.js/common/src/tools/timestamps.tools";
import { DAYS, TsDate, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { TODAY } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { add, areSame, beginOf, daysInMonth, endOf, fromDateObject, getTodayDate, set, toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { ButtonStyle, CjsButton } from "../../components/button/button";
import { Picker, PickerOptions } from "../picker";
import { I18nCalendar } from "./calendar.i18n";

/** List of supported events */
type DayCellEvent = "click" | "hover";

/** Type used to dispatch cellEvent */
export type DayCellEventData = { evt: DayCellEvent, date: UnixTimestamp };

/** 
 * Position of badge relative to
 */
export enum CalendarBadgePosition {
    TOP_RIGHT = "top-right",
    TOP_LEFT = "top-left",
    BOTTOM_RIGHT = "bottom-right",
    BOTTOM_LEFT = "bottom-left"
}

/** 
 * Define a CalendarBadge, to add information on top/bottom/right/left corner of a day cell 
 */
export interface CalendarBadge {

    /** Date where the badge should be applied */
    date: UnixTimestamp;

    /** Label of badge */
    label: string;

    /** 
     * Position of badge relative to day cell
     * Default: TOP_RIGHT
     */
    position?: CalendarBadgePosition;

    /** Css classes to apply to badge div */
    cssClasses?: string[];
}

/** 
 * Define a CalendarCell, to add information on a day cell 
 */
export interface CalendarCellStyle {

    /** Date where the cell style should be applied */
    date: UnixTimestamp;

    /** Css classes to apply to badge div */
    cssClasses?: string[];

    /** Css style of cellStyling */
    style?: CssStyleFields;
}


/** Parameters for Calendar */
export interface CalendarOptions extends PickerOptions {

    /** Add a bottom grey border under the week header */
    weekHeaderBorder: boolean;

    /** Make day cells square or rounded */
    roundedCells: boolean;

    /**
     * Select the first day of a week (use AppService lang by default)
     * Default: setup weekStartOn from user browser locale prefs
     */
    calendarWeekStartOn?: CalendarWeekStartOn;

    /**
     * Define witch ISO days of week should be highlighted
     * -> [6,7] == both saturday and sunday highlighted (default)
     * -> [6] == saturday is highlighted
     * -> [7] == sunday is highlighted
     */
    highLightDays: number[];

    /**
     * Display or not the calendars weeks
     */
    calendarWeeks: boolean;

    /**
     * Display or not an indicator for the today date
     */
    displayTodayDate: boolean;

    /**
     * Display cells for other months
     */
    displayOtherMonthsDays: boolean;

    /**
     * Enable the height resizing for component. The component will take less height as possible. Grow with the number of days per month (rows)
     * Disabled, the min-height of dayGrid wil be set to a calculated max "possible" height
     */
    enableHeightResize: boolean;

    /**
     * Show a button to reset current date to today
     */
    enableTodayReset: boolean;

    /** Badges to add relative to dayCell */
    badges?: CalendarBadge[];

    /** Styling to apply on a given cell for a specific date */
    calendarCellStyles?: CalendarCellStyle[];

    /** Fix calendar to given date, user can't change month / year with arrows or month selector */
    isFixedCalendar?: boolean;

    /** If true, display year in calendar header text */
    displayYear?: boolean;

    /**
     * Month Year text horizontal alignment
     * @default TEXT_ALIGN.CENTER
     */
    monthYearTextAlign: TEXT_ALIGN.START | TEXT_ALIGN.CENTER | TEXT_ALIGN.END;

    /** List of cell events that should trigger a calendar.dayCellEvent */
    eventsOnDay?: DayCellEvent[];
}

/** Parameters for Calendar */
export const defaultCalendarOptions: CalendarOptions = {

    /** Add a bottom grey border under the week header */
    weekHeaderBorder: false,

    /** Make day cells square or rounded */
    roundedCells: true,

    /**
     * Define witch ISO days of week should be highlighted
     * -> [6,7] == both saturday and sunday highlighted (default)
     * -> [6] == saturday is highlighted
     * -> [7] == sunday is highlighted
     */
    highLightDays: [6, 7],

    /**
     * Display or not the calendars weeks
     */
    calendarWeeks: true,

    /**
     * Display or not an indicator for the today date
     */
    displayTodayDate: true,

    /**
     * Display cells for other months
     */
    displayOtherMonthsDays: true,

    /**
     * Enable the height resizing for component. The component will take less height as possible. Grow with the number of days per month (rows)
     * Disabled, the min-height of dayGrid wil be set to a calculated max "possible" height
     */
    enableHeightResize: false,

    /**
     * Show a button to reset current date to today
     */
    enableTodayReset: false,

    /** Fix calendar to given date, user can't change month / year with arrows or month selector */
    isFixedCalendar: false,

    /** If true, display year in calendar header text */
    displayYear: true,

    /**
     * Month Year text horizontal alignment
     * @default TEXT_ALIGN.CENTER
     */
    monthYearTextAlign: TEXT_ALIGN.CENTER,

    /** @inheritdoc */
    disabled: false
};

/** Labels fields for Calendar */
export interface CalendarLabels {

    /** Complete days names list */
    weekdays: string[];

    /** Abbreviation days names list
     * ex: (Monday => Mon / Wednesday => Wed)
     */
    weekdays_abbr: string[];

    /** Complete month names list */
    months: string[];

    /** Abbreviation month names list
     * ex: (February => Feb / August => Aug)
     */
    months_abbr: string[];
}

/** Use to add date field to a DivElement*/
type DateElement = {
    date: TsDate,
    subDivs: {
        topLeft: HTMLDivElement,
        topRight: HTMLDivElement,
        bottomRight: HTMLDivElement,
        bottomLeft: HTMLDivElement
    }
}
export type HTMLDateDivElement = HTMLDivElement & DateElement;

/** Starting day of a week available values */
export type CalendarWeekStartOn = "monday" | "sunday";

interface GridBuilderValues {
    cellsCount: number;
    rowIndex: number;
}

export type CalendarPickerMode = "days" | "months" | "years";

// Class names constants
export const DAY_CELL = "day-cell";
export const DAY_CELL_TODAY = "today";
export const DAY_CELL_ROUNDED = "cell-rounded";
export const DAY_CELL_OTHER_MONTH = "other-month";
export const DAY_CELL_HIGHLIGHT = "highlight";
export const DAY_CELL_HIGHLIGHT_OTHER_MONTH = "highlight-other-month";
export const DAY_CELL_BADGE = "day-cell-badge";
export const DAY_CELL_IS_CLICKED = "is-clicked";
export const CALENDAR_WEEKS_MARGIN = "calendar-weeks-margin";
export const WEEKHEADER_BOTTOM_BORDER = "weekHeader-bottom-border";
export const MONTH_HEADER_BTN_ACTIVE_PRESSED = "active-pressed";
export const FIXED_CALENDAR = "fixed-calendar";

export const ROW_HEIGHT = 40;
export const GRID_WIDTH = 280;
export const CALENDAR_WEEKS_MARGIN_VALUE = 30;

@CustomElement({
    name: "cjs-calendar",
    style: require("./calendar.scss"),
    template: require("./calendar.html"),
    i18n: require("./calendar.i18n").I18n,
    elementCssClasses: [FLEX_CONTAINER_COLUMN]
})
export class CjsCalendar extends Picker<UnixTimestamp, I18nCalendar> {

    /** When navigation in the calendar done */
    @Event(NoBubblesEventInit)
    public readonly calendarNavigationEvent!: EventHandler<void>;

    /**
     * This event is emitted only if options.eventsOnDay is set
     */
    @Event(NoBubblesEventInit)
    public readonly dayCellEvent!: EventHandler<DayCellEventData>;

    @TypedWebComponentOptions(defaultCalendarOptions)
    public override readonly options!: WebComponentOptions<CalendarOptions>;

    @TypedHtmlElements()
    public readonly components!: {
        monthHeader: HTMLElement,
        weekHeader: HTMLElement,
        dayGrid: HTMLElement,
        monthYearPicker: HTMLElement,
        todayBtn: HTMLDivElement
    };

    /**
     * The ts used in calendar
     * Bind with currentDate
     */
    private _currentTs = TODAY();

    /** Get current ts */
    public get currentTs(): UnixTimestamp {
        return this._currentTs;
    }

    /** Set current ts */
    public set currentTs(value: UnixTimestamp) {
        this._currentTs = value;
        this._currentDate = toDate(value);
    }

    /** Current date of calendar */
    private _currentDate = toDate(TODAY());

    /** 
     * Get current date of calendar
     * Bind with currentTs
     */
    public get currentDate(): TsDate {
        return this._currentDate;
    }

    /** Set current date of calendar */
    public set currentDate(value: TsDate) {
        this._currentDate = value;
        this.currentTs = this._currentDate.uts;
    }

    /** Store current cells displayed into calendar */
    public dayCells: HTMLDateDivElement[] = [];

    /** Current mode of Calendar duration selector */
    protected _pickerMode: CalendarPickerMode = "days";

    /** Get the current picker mode */
    public get pickerMode(): CalendarPickerMode {
        return this._pickerMode;
    }

    /** Maximum index of week (firstDay=0 / lastDay=6=MAX_WEEK_DAY_INDEX) */
    protected readonly MAX_WEEK_DAY_INDEX = 6;

    /** Number of maximum rows possible for dayGrid */
    protected readonly MAX_ROW_COUNT = 6;

    /** Click pressed debounce value */
    protected readonly MOUSE_CLICK_DEBOUNCE = 350;

    /** Current number of rows displayed for dayGrid */
    protected _rowCount = 0;

    constructor(options?: Partial<CalendarOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Init component after it is inserted in the dom
     */
    public override init(): void {
        this.components.todayBtn.tabIndex = 0;
        // Init listeners
        this._initListeners();
    }

    /** 
     * Refresh component, re-render component with options
     */
    public override refresh(): void {
        super.refresh();

        // Refresh weekStartOn option
        this._refreshWeekStartOn();

        // Set up component month header
        this._setupMonthHeader();
        this._setupWeekHeader();
        this._setupDaysGrid();

        // Refresh week border
        this._refreshWeekHeaderBorder();

        // Unset the height resizing for Calendar
        this._refreshEnableHeightResize();

        // Add css class for calendar weeks margin
        this._refreshCalendarWeeks();

        // Today reset
        this._refreshTodayReset();

        // Refresh cells styling
        this._refreshCellsStyling();
    }

    /** Refresh badges of dayCells for calendar */
    protected _refreshBadges(): void {
        if (!this.options.badges || !this.options.badges.length) { return; }

        for (const badge of this.options.badges) {

            const dayCell = this.dayCells.find(cell => areSame(cell.date, badge.date, "day"));

            if (!dayCell || !dayCell.subDivs) { continue; }

            const position = badge.position ?? CalendarBadgePosition.TOP_RIGHT;
            const badgeDiv = createHtmlElement("div", {
                cssClass: [DAY_CELL_BADGE, position],
                innerHTML: badge.label
            });

            if (badge.cssClasses) {
                badgeDiv.classList.add(...badge.cssClasses);
            }

            // Add content to correct div
            switch (position) {
                case CalendarBadgePosition.TOP_LEFT:
                    dayCell.subDivs.topLeft.append(badgeDiv);
                    break;
                case CalendarBadgePosition.BOTTOM_RIGHT:
                    dayCell.subDivs.bottomRight.append(badgeDiv);
                    break;
                case CalendarBadgePosition.BOTTOM_LEFT:
                    dayCell.subDivs.bottomLeft.append(badgeDiv);
                    break;
                case CalendarBadgePosition.TOP_RIGHT:
                default:
                    dayCell.subDivs.topRight.append(badgeDiv);
            }
        }
    }

    /** Refresh css styling of dayCells for calendar */
    protected _refreshCellsStyling(): void {
        if (!this.options.calendarCellStyles || !this.options.calendarCellStyles.length) { return; }

        for (const cellStyle of this.options.calendarCellStyles) {
            const dayCell = this.dayCells.find(d => areSame(cellStyle.date, d.date));
            if (!dayCell) { continue; }

            // Don't highlight other months cells if option is disable
            if (!this.options.displayOtherMonthsDays && dayCell.classList.contains(DAY_CELL_OTHER_MONTH)) {
                continue;
            }

            if (cellStyle.cssClasses) {
                setHtmlElementOptions(dayCell, {
                    cssClass: cellStyle.cssClasses
                });
            }

            if (cellStyle.style) {
                setHTMLElementStyle(dayCell, cellStyle.style);
            }
        }
    }

    /** Refresh todayReset from options */
    protected _refreshTodayReset(): void {
        toggleVisible(this.components.todayBtn, this.options.enableTodayReset, Display.FLEX);
        if (this.options.enableTodayReset) {
            this.components.todayBtn.addEventListener("click", this._resetTodayDateListener, true);
        } else {
            // Remove event to disable any possible action
            this.components.todayBtn.removeEventListener("click", this._resetTodayDateListener, true);
        }
    }

    /** Refresh weekHeaderBorder from options */
    protected _refreshWeekHeaderBorder(): void {
        // Display the bottom grey border for weekHeader
        toggleClass(this.components.weekHeader, this.options.weekHeaderBorder, WEEKHEADER_BOTTOM_BORDER);
    }

    /** Refresh enableHeightResize from options */
    protected _refreshEnableHeightResize(): void {
        /**
         * Set a min height for dayGrid component <=> disable height resizing for month with
         * low number of days
         */
        if (this.options.enableHeightResize) { return; }

        this.components.dayGrid.style.minHeight = this.MAX_ROW_COUNT * ROW_HEIGHT + "px";
    }

    /** Refresh calendarWeeks behavior */
    protected _refreshCalendarWeeks(): void {
        toggleClass(this.components.weekHeader, this.options.calendarWeeks, CALENDAR_WEEKS_MARGIN);
        toggleClass(this.components.dayGrid, this.options.calendarWeeks, CALENDAR_WEEKS_MARGIN);

        if (this.options.calendarWeeks) {
            // Set month header width to ("daygrid_with" + "CALENDAR_WEEKS_MARGIN_class.margin-left")
            this.components.monthHeader.style.width = GRID_WIDTH + CALENDAR_WEEKS_MARGIN_VALUE + "px";
        } else {
            this.components.monthHeader.style.width = GRID_WIDTH + "px";
        }
    }

    /** Init containers listeners */
    protected _initListeners(): void {

        // Disable click listeners for container div
        this.addEventListener("click", e => e.stopPropagation());

        // Disable touchstart listeners for container div
        this.addEventListener("touchstart", e => e.stopPropagation());

    }

    /**
     * Refresh weekStartOnOption by using AppService lang if pickerLang option is not set
     * By default : week starts on Sunday if lang is "en" & week starts on Monday if lang is "fr"
     */
    protected _refreshWeekStartOn(): void {
        if (this.options.calendarWeekStartOn) {
            this.options.setWithoutRefresh({ calendarWeekStartOn: this.options.calendarWeekStartOn });
        } else {
            const startOnDay = beginOf(this.currentDate, "week").weekday;
            this.options.setWithoutRefresh({ calendarWeekStartOn: startOnDay === DAYS.SUNDAY ? "sunday" : "monday" });
        }
    }

    /**
     * Set up month header display for CalendarComponent
     */
    protected _setupMonthHeader(): void {

        // Create month header text
        const monthYearTextDiv = createHtmlElement("div", {
            attributes: {
                [REF_ATTRIBUTE]: "monthYearText",
                tabIndex: "0"
            },
            cssClass: [FLEX_CONTAINER_CENTERED]
        });

        this._updateMonthYearHeaderText(monthYearTextDiv);

        // Add click listener => show monthYearPicker
        monthYearTextDiv.addEventListener("click", () => this._onMonthYearDivClick());
        monthYearTextDiv.addEventListener("keydown", (event) => {
            if (event.key === KeyboardInput.SPACE) {
                this._onMonthYearDivClick();
            }
        });

        // Create month header buttons
        const backwardButton: CjsButton = this._createChangeMonthButton(-1, monthYearTextDiv);
        const forwardButton: CjsButton = this._createChangeMonthButton(+1, monthYearTextDiv);

        // Appends buttons and text
        empty(this.components.monthHeader);
        this.components.monthHeader.append(monthYearTextDiv);

        if (!this._isFixedCalendar()) {
            this.components.monthHeader.prepend(backwardButton);
            this.components.monthHeader.append(forwardButton);
        }

        toggleClass(this.components.monthHeader, this._isFixedCalendar(), FIXED_CALENDAR);
    }


    /** 
     * Set up weeks header for CalendarComponent
     */
    protected _setupWeekHeader(): void {

        // Empty element for refresh
        empty(this.components.weekHeader);

        // Get days keys
        const daysKeys = Object.keys(DAYS).filter(key => isNumber(getProp(DAYS, key)));

        // Loop for all days of a week
        for (const dayKey of daysKeys) {

            // Create element
            const weekday = createHtmlElement("div", { cssClass: "weekday" });

            // Get the correct day index from enum
            let dayEnumIndex = getProp<number>(DAYS, dayKey);

            // For sunday, rotate indexes of enum (1,2,...,7 => 7,1,2,...,6)
            if (this.options.calendarWeekStartOn === "sunday") {
                dayEnumIndex = getProp<number>(DAYS, dayKey) === DAYS.MONDAY ? DAYS.SUNDAY : getProp<number>(DAYS, dayKey) - 1;
            }

            const dayText = this.i18n.weekdays_abbr[dayEnumIndex];
            weekday.textContent = dayText;

            // Add element
            this.components.weekHeader.append(weekday);
        }
    }

    /** 
     * Set up monthYear cells for monthYearPicker
     */
    protected _setupMonthYearPicker(): void {

        const monthYearPicker = this.components.monthYearPicker;

        if (!monthYearPicker) { return; }

        // Remove the grid if already exist
        empty(monthYearPicker);

        // Init grid values
        let col = 1;
        let row = 1;
        setDisplay(monthYearPicker, Display.GRID);

        if (this._pickerMode === "months") {

            // Loop into months labels for current language
            for (let i = 0; i < this.i18n.months_abbr.length; i++) {

                const month = this.i18n.months_abbr[i];

                // Create the cell
                const monthCell = this._createMonthYearCell(month, String(i + 1));

                // Set the currentDate cell month display "is-clicked"
                if (this.currentDate.month === i) { monthCell.classList.add("is-clicked"); }

                // Set grid position for cell
                monthCell.style.gridColumn = String(col);
                monthCell.style.gridRow = String(row);

                // Add cell to monthYearPicker
                monthYearPicker.append(monthCell);

                // Update the grid values (0 < col < 4 & 0 < row < 4)
                col++;
                if (col % 5 === 0) {
                    row++;
                    col = 1;
                }
            }
        } else if (this._pickerMode === "years") {

            const fromYear = this._getYearRange().fromYear;
            const toYear = this._getYearRange().toYear;

            for (let year = fromYear; year <= toYear; year++) {

                // Create the cell
                const yearCell = this._createMonthYearCell(String(year), String(year));

                // Set the currentDate cell year display "is-clicked"
                if (this.currentDate.year === year) { yearCell.classList.add("is-clicked"); }

                // Set grid position for cell
                yearCell.style.gridColumn = String(col);
                yearCell.style.gridRow = String(row);

                // Add cell to monthYearPicker
                monthYearPicker.append(yearCell);

                // Update the grid values (0 < col < 4 & 0 < row < 4)
                col++;
                if (col % 5 === 0) {
                    row++;
                    col = 1;
                }

            }
        }
    }

    /** Build a monthYearPicker cell */
    protected _createMonthYearCell(text: string, value: string): HTMLElement {
        const div = createHtmlElement("div", {
            innerHTML: text,
            cssClass: ["month-year-picker-cell", "selectable"],
            attributes: {
                value,
                tabIndex: "0"
            }
        });

        div.addEventListener("click", () => this._changeMonthYearCellCallback(div));
        div.addEventListener("keydown", (event) => {
            if (event.key === KeyboardInput.SPACE) {
                this._changeMonthYearCellCallback(div);
            }
        });
        return div;
    }

    /**
     * When user click on cell of monthYearPicker, used to go down in the month/year selection
     * Move: years => months => days
     * @param divClicked - the div clicked by the user
     */
    protected _changeMonthYearCellCallback(divClicked: HTMLElement): void {

        const value = divClicked.getAttribute("value");

        if (this._pickerMode === "months") {
            // For months, set the month clicked for currentDate
            this.currentTs = set(this.currentDate, { month: Number(value) }).uts;
            this._pickerMode = "days";
        } else if (this._pickerMode === "years") {
            // For years, set the year clicked for currentDate
            this.currentTs = set(this.currentDate, { year: Number(value) }).uts;
            this._pickerMode = "months";
        }

        // Refresh the display on simulating monthYearPicker click behavior
        this._toggleMonthYearPicker();
    }

    /** 
     * Set up days grid for CalendarComponent
     */
    protected _setupDaysGrid(): void {

        const dayGridDiv: HTMLElement | null = this.components.dayGrid;

        // Check if grid div exist
        if (!dayGridDiv) { return; }

        // Remove the grid if already exist
        empty(dayGridDiv);
        this.dayCells = [];

        this._rowCount = 0;
        const maxCellsCount = this.MAX_ROW_COUNT * 7;
        let gridBuilderValues: GridBuilderValues = {
            cellsCount: 0,// Count of cells displayed 
            rowIndex: 1// Count of rows displayed 
        };

        // Get month start/end/length values
        const startDateOfMonth: TsDate = beginOf(this.currentDate, "month");
        const endDateOfMonth: TsDate = endOf(this.currentDate, "month");
        const daysInMonthCount: number = daysInMonth(this.currentDate);

        let startMonthDayIndex = startDateOfMonth.weekday % 7;// sunday =>0
        let endMonthDayIndex = endDateOfMonth.weekday % 7;// sunday =>0

        /* 
         * ISO week start with "sunday": index = 0, 
         * To start week with monday:
         * - shift other days indexes
         * - for sunday, change index from 0 to 6
         */
        if (this.options.calendarWeekStartOn === "monday") {
            startMonthDayIndex = startMonthDayIndex === 0 ? this.MAX_WEEK_DAY_INDEX : startMonthDayIndex - 1;
            endMonthDayIndex = endMonthDayIndex === 0 ? this.MAX_WEEK_DAY_INDEX : endMonthDayIndex - 1;
        }

        // Build days of previous month
        for (let i = startMonthDayIndex; i > 0; i--) {

            const cell = this._createOtherMonthCell(-i, 0);
            this._addCellSubDivs(cell);

            gridBuilderValues = this._setCellGridPosition(cell, gridBuilderValues);
            this.dayCells.push(cell);
            dayGridDiv.append(cell);
        }

        // Build days of current month
        for (let i = 0; i < daysInMonthCount; i++) {

            // Create cell
            const dayCell = createHtmlElement("div", {
                cssClass: [DAY_CELL, FLEX_CONTAINER, FLEX_WRAP.WRAP, POSITION.RELATIVE]
            }) as HTMLDateDivElement;
            this._addCellSubDivs(dayCell);

            dayCell.date = fromDateObject({ year: this.currentDate.year, month: this.currentDate.month, day: 1 + i });


            const dayCellNumber = createHtmlElement("div", {
                cssClass: [W_100, H_100, POSITION.ABSOLUTE],
                innerHTML: String(dayCell.date.day)
            });


            dayCell.append(dayCellNumber);
            // Add behaviors and display
            this._setCellDisplay(dayCell);
            // Add event as needed
            this._installDayCellEventBasedOnOptions(dayCell);

            // Add to grid div
            gridBuilderValues = this._setCellGridPosition(dayCell, gridBuilderValues);
            this.dayCells.push(dayCell);
            dayGridDiv.append(dayCell);
        }


        // Build days of next month
        for (let i = 0; i < this.MAX_WEEK_DAY_INDEX - endMonthDayIndex; i++) {
            // Limit cells to build to 42
            if (gridBuilderValues.cellsCount >= maxCellsCount) { break; }

            const cell = this._createOtherMonthCell(+i, +1);
            this._addCellSubDivs(cell);

            gridBuilderValues = this._setCellGridPosition(cell, gridBuilderValues);
            this.dayCells.push(cell);
            dayGridDiv.append(cell);
        }

        // Fill with empty cells to fill the height empty space
        if (!this.options.enableHeightResize && this._rowCount < this.MAX_ROW_COUNT) {

            // Get the number of rows to add
            const rowsToAdd = this.MAX_ROW_COUNT - this._rowCount;

            const lastCell: HTMLDateDivElement = dayGridDiv.lastChild as HTMLDateDivElement;

            // Create the row
            for (let i = 0; i < rowsToAdd; i++) {

                // Create the week days
                for (let j = 0; j <= this.MAX_WEEK_DAY_INDEX; j++) {
                    // Limit cells to build to 42
                    if (gridBuilderValues.cellsCount >= maxCellsCount) { break; }

                    // Check if the lastChild of grid is same month than current one displayed, return the offset if not
                    const dayOffset = lastCell && this.currentDate.yearAndMonth !== lastCell.date.yearAndMonth ? lastCell.date.day : 0;

                    // Build cell
                    const cell = this._createOtherMonthCell(i * 7 + j + dayOffset, 1);
                    this._addCellSubDivs(cell);

                    gridBuilderValues = this._setCellGridPosition(cell, gridBuilderValues);
                    this.dayCells.push(cell);
                    dayGridDiv.append(cell);
                }
            }
        }

        // Refresh cells styles
        this._refreshCellsStyling();

        // Refresh badges
        this._refreshBadges();
    }

    /** On click event on monthYearDiv */
    protected _onMonthYearDivClick(): void {
        if (this._isFixedCalendar()) { return; }

        this._updateCalendarMode();
        this._updateMonthYearHeaderText();
        this._toggleMonthYearPicker();
    }

    /** Toggle the monthYearPicker display, based on the pickerMode value */
    protected _toggleMonthYearPicker(): void {

        // Always update monthYearHeader text
        this._updateMonthYearHeaderText();

        if (this._pickerMode === "days") {
            // If current pickerMode is "days", hide the monthYearPicker, and display/refresh the dayGrid
            setDisplay(this.components.dayGrid, Display.GRID);
            setDisplay(this.components.weekHeader, Display.FLEX);
            setDisplay(this.components.monthYearPicker, Display.NONE);

            this._setupDaysGrid();

        } else {
            // If current pickerMode is "months" || "years", hide the dayGrid, and display/refresh the monthYearPicker
            this.components.monthYearPicker.style.height = toPx(this._calcGridContainerHeight());
            setDisplay(this.components.dayGrid, Display.NONE);
            setDisplay(this.components.weekHeader, Display.NONE);

            this._setupMonthYearPicker();
        }
    }

    /** 
     * Create a button for month header
     *
     * @param offset (positive or negative) number of months to go forward/backward from the current date
     * @param monthYearDiv The div to set textContent attribute
     */
    protected _createChangeMonthButton(offset: number, monthYearDiv: HTMLElement): CjsButton {

        // Create button
        const changeMonthButton = new CjsButton({
            icon: {
                name: offset < 0 ? "fa-chevron-left" : "fa-chevron-right",
                fixedWidth: true
            },
            iconMode: "only",
            outline: true,
            cssStyle: ButtonStyle.NONE,
            cssClasses: ["change-month-button"]
        });

        // Button click behavior
        this._addChangeMonthClickListener(changeMonthButton, offset, monthYearDiv);

        return changeMonthButton;
    }

    /**
     * Create a cell of an other month than the current one
     *
     * @param dayOffset Offset of days from
     * @param monthOffset Offset of month from 
     */
    protected _createOtherMonthCell(dayOffset: number, monthOffset: number): HTMLDateDivElement {

        // Create cell
        const dayCell: HTMLDateDivElement = createHtmlElement("div", {
            cssClass: [DAY_CELL, DAY_CELL_OTHER_MONTH, FLEX_CONTAINER, FLEX_WRAP.WRAP, POSITION.RELATIVE]
        }) as HTMLDateDivElement;

        dayCell.date = add(fromDateObject({ year: this.currentDate.year, month: this.currentDate.month, day: 1 }), { month: monthOffset, day: dayOffset });

        // If other month cells should be display, set up event behaviors and display
        if (this.options.displayOtherMonthsDays) {
            const dayCellNumber = createHtmlElement("div", {
                cssClass: [W_100, H_100, POSITION.ABSOLUTE],
                innerHTML: String(dayCell.date.day)
            });
            dayCell.append(dayCellNumber);

            // Set up display
            this._setCellDisplay(dayCell);

            // Install listeners for dayCellEvent
            this._installDayCellEventBasedOnOptions(dayCell);

        } else {
            // Else, just display the calendar week if needed
            this.setCalendarWeekCellDisplay(dayCell);
        }

        return dayCell;
    }

    /**
     * Set grid position for a cell
     */
    protected _setCellGridPosition(cell: HTMLDateDivElement, gridBuilderValues: GridBuilderValues): GridBuilderValues {

        // Update cellsCounter
        gridBuilderValues.cellsCount++;

        // If it's the 7th cell, force modulo result to 7 index
        const columnIndex = gridBuilderValues.cellsCount % 7 === 0 ? 7 : gridBuilderValues.cellsCount % 7;

        // Set grid column and row
        cell.style.gridColumn = columnIndex.toString();
        cell.style.gridRow = gridBuilderValues.rowIndex.toString();

        // Update row when it's the 7th call added to the row
        if (columnIndex % 7 === 0) {
            gridBuilderValues.rowIndex++;
            this._rowCount++;
        }

        return gridBuilderValues;
    }

    /**
     * Set date cells behaviors and displays
     */
    protected _setCellDisplay(cell: HTMLDateDivElement): void {

        // Set up border radius display
        toggleClass(cell, this.options.roundedCells, DAY_CELL_ROUNDED);

        // Highlight weekend cells days
        if (this.options.highLightDays?.includes(cell.date.weekday)) {
            // Determine witch class to add to cell
            let classToAdd = "";
            if (cell.classList.contains(DAY_CELL_OTHER_MONTH)) {
                classToAdd = DAY_CELL_HIGHLIGHT_OTHER_MONTH;
            } else {
                classToAdd = DAY_CELL_HIGHLIGHT;
            }

            // Add class from highlighDays params
            cell.classList.add(classToAdd);
        }
        // Set calendar week display
        this.setCalendarWeekCellDisplay(cell);

        // Today date display
        if (cell.date.yearAndOrdinal === getTodayDate().yearAndOrdinal && this.options.displayTodayDate) {
            cell.classList.add(DAY_CELL_TODAY);
        }

        // If the selectedDate is equal to cellDate, add isClicked display
        if (this._value && toDate(this._value).yearAndOrdinal === cell.date.yearAndOrdinal) {
            cell.classList.add(DAY_CELL_IS_CLICKED);
        } else {
            cell.classList.remove(DAY_CELL_IS_CLICKED);
        }

    }

    /** Install listeners for dayCellEvent */
    private _installDayCellEventBasedOnOptions(cell: HTMLDateDivElement): void {
        if (this.options.eventsOnDay) {
            // Set up chained of event
            for (const event of this.options.eventsOnDay) {
                cell.addEventListener(event, () => {
                    this.dayCellEvent.emit({ evt: event, date: cell.date.uts });
                });
            }
        }
    }

    /**
     * Add a "change month" click behavior for an element
     * @param element Element to add click listener
     * @param offset Month offset, can be a number (-1/+1) or a date from a different month than current month displayed
     * @param monthYearDiv [optional] The div to set textContent 
     */
    protected _addChangeMonthClickListener(element: HTMLElement, offset: number | TsDate, monthYearDiv?: HTMLElement | null): void {

        // Get the offset value from a Date (compare offsetDate with currentDate)
        if (!isNumber(offset)) {
            offset = compareAsc(offset.uts, this.currentDate.uts);
        }

        // Init mousedown behavior values
        let mouseDownInterval: number | null;
        let mouseUpListening = false;

        const onMouseDown = (): void => {

            if (!mouseUpListening) {
                // Add click listener
                document.body.addEventListener("mouseup", onMouseUp);
                mouseUpListening = true;
            }

            element.classList.add(MONTH_HEADER_BTN_ACTIVE_PRESSED);

            // Set up an interval
            mouseDownInterval = window.setInterval(() => {

                // Call callback each {{MOUSE_CLICK_DEBOUNCE}} ms
                this._changeMonthCallback(offset, monthYearDiv);

            }, this.MOUSE_CLICK_DEBOUNCE);
        };

        const onMouseUp = (): void => {

            if (mouseDownInterval) {
                // Remove interval
                window.clearInterval(mouseDownInterval);
            }

            // Reset class
            element.classList.remove(MONTH_HEADER_BTN_ACTIVE_PRESSED);

            // Reset mousedown behavior values
            mouseDownInterval = null;
        };

        // Add click listener mousedown
        element.addEventListener("mousedown", onMouseDown);
        element.addEventListener("click", () => {

            // Set date in element is a cell in dayGrid
            if ("date" in element && (element as HTMLDateDivElement).date != null) {
                this.currentTs = (element as HTMLDateDivElement).date.uts;
            }

            this._changeMonthCallback(offset, monthYearDiv);
        });

    }

    /**
     * change month behavior for an element
     * @param offset Month offset, can be a number (-1/+1) or a date from a different month than current month displayed
     * @param monthYearDiv [optional] The div to set textContent 
     */
    protected _changeMonthCallback(offset: number | TsDate, monthYearDiv?: HTMLElement | null): void {

        // Update currentDate based on pickerMode
        switch (this._pickerMode) {
            case "days":
                // Add +/-1 month to current date
                this.currentTs = add(this.currentTs, { month: Number(offset) }).uts;

                // Refresh days grid
                this._setupDaysGrid();
                break;
            case "months":
                // Add +/-1 years to current date
                this.currentTs = add(this.currentTs, { year: Number(offset) }).uts;

                // Useless to update monthYearPicker here, months never change between years
                break;
            case "years":
                // Add +/-12 years to current date
                this.currentTs = add(this.currentTs, { year: Number(offset) * MONTHS_IN_ONE_YEAR }).uts;

                // Refresh monthYearPicker
                this._setupMonthYearPicker();
                break;
            default:
                this.currentTs = add(this.currentTs, { month: Number(offset) }).uts;

                // Refresh days grid
                this._setupDaysGrid();
                break;
        }

        // Update month text
        this._updateMonthYearHeaderText(monthYearDiv);
        this.calendarNavigationEvent.emit();
    }


    /** Update the header text value for monthYearDiv
     * Based on the currentDate value
     */
    protected _updateMonthYearHeaderText(monthYearDiv?: HTMLElement | null): void {
        if (!monthYearDiv) {
            monthYearDiv = findElementByRef(this, "monthYearText");
        }
        if (!monthYearDiv) {
            return;
        }
        monthYearDiv.textContent = this._getMonthHeaderText();
        monthYearDiv.classList.add(this.options.monthYearTextAlign);
        // Enable W_100 for other alignments than center
        toggleClass(monthYearDiv, this.options.monthYearTextAlign !== TEXT_ALIGN.CENTER, W_100);
    }

    /**
     * Get years range for monthYearPicker - Year mode -
     * Get 12 years range, from -5y to currentDate and +6y from currentDate
     */
    protected _getYearRange(): { fromYear: number, toYear: number } {
        const fromYear = this.currentDate.year - 5;
        const toYear = this.currentDate.year + 6;
        return { fromYear, toYear };
    }

    /**
     * Return a formatted "Month YYYY" or "YYYY" string of current displayed month
     * Same display for days & month pickerMode
     */
    protected _getMonthHeaderText(): string {

        let label = "";
        switch (this._pickerMode) {
            case "days":
                label = this.i18n.months[this.currentDate.month - 1] + (this.options.displayYear ? " " + this.currentDate.year : "");
                break;
            case "months":
                label = String(this.currentDate.year);
                break;
            case "years":
                label = this._getYearRange().fromYear.toString() + "-" + this._getYearRange().toYear.toString();
                break;
            default:
                label = this.i18n.months[this.currentDate.month - 1] + " " + this.currentDate.year;
                break;
        }
        return label;
    }

    /** 
     * Add bottom border display for the weekHeader component
     */
    protected _setWeekHeaderBottomBorder(): void {
        this.components.weekHeader.classList.add(WEEKHEADER_BOTTOM_BORDER);
    }

    /**
     * Set a min height for dayGrid component <=> disable height resizing for month with
     * low number of days
     */
    protected _unsetHeightResizing(): void {
        this.components.dayGrid.style.minHeight = this.MAX_ROW_COUNT * ROW_HEIGHT + "px";
    }

    /** 
     * Update the pickerMode value, use to go up when user click on monthYearHeaderDiv
     * Move: days => months => years
     */
    protected _updateCalendarMode(): void {
        switch (this._pickerMode) {
            case "months":
                this._pickerMode = "years";
                break;
            case "years":
            case "days":
                this._pickerMode = "months";
                break;
            default:
                this._pickerMode = "days";
        }
    }

    /**
     * Check if the cell in parameter is a cell for an other month
     * @param cell Cell to check
     */
    protected _isOtherMonthCell(cell: HTMLDateDivElement): boolean {
        return cell.classList.contains(DAY_CELL_OTHER_MONTH);
    }

    /** Return the hypothetic dayGrid height */
    protected _calcGridContainerHeight(): number {
        return this.options.enableHeightResize ? this._rowCount * ROW_HEIGHT + ROW_HEIGHT : ROW_HEIGHT * this.MAX_ROW_COUNT + ROW_HEIGHT; // (+ROW_HEIGHT is for weekHeader row)
    }

    /** Reset date to today */
    protected _resetTodayDate(): void {
        this.currentTs = TODAY();
        this._value = this.currentTs;

        // Update grid
        this.setGridByTs(this.currentTs);

        // Refresh selected day display
        this._setupDaysGrid();

        // Reset picker mode
        this._pickerMode = "days";
        this._toggleMonthYearPicker();
    }

    /** Event Listener Object wrapping this._resetTodayDate call */
    protected _resetTodayDateListener: EventListenerObject = {
        handleEvent: () => {
            this._resetTodayDate();
        }
    };

    /** Set up calendar week display for a defined cell */
    protected setCalendarWeekCellDisplay(cell: HTMLDateDivElement): void {

        // Display "calendar week number" beside first day cell of a week
        if (this.options.calendarWeeks && (
            this.options.calendarWeekStartOn === "monday" && cell.date.weekday === DAYS.MONDAY ||
            this.options.calendarWeekStartOn === "sunday" && cell.date.weekday === DAYS.SUNDAY
        )) {
            cell.classList.add("is-first-day");
            cell.setAttribute("calendar-week-count", String(cell.date.weekNumber));
        }

    }
    /**
     * update grid from this value of date
     * @param ts value of date to get corresponding grid
     */
    public setGridByTs(ts: UnixTimestamp): void {

        const prevDate = this.currentDate;

        // Update current date of calendar

        this.currentDate = toDate(ts);

        if (this.currentDate.yearAndMonth !== prevDate.yearAndMonth) {
            // month and year has changed
            this._setupDaysGrid();

            // Update month text if already generated (in first init()) otherwise it will be called
            this._updateMonthYearHeaderText();

        }
    }

    /** @returns true if calendar is fixed on current date month/year */
    private _isFixedCalendar(): boolean {
        return Boolean(this.options.isFixedCalendar);
    }

    /** Add sub div to cell */
    protected _addCellSubDivs(cell: HTMLDateDivElement): void {
        const topLeft = createHtmlElement("div", {
            cssClass: [W_50, H_50],
            attributes: {
                [REF_ATTRIBUTE]: "topLeft"
            }
        });

        const topRight = createHtmlElement("div", {
            cssClass: [W_50, H_50],
            attributes: {
                [REF_ATTRIBUTE]: "topRight"
            }
        });
        const bottomRight = createHtmlElement("div", {
            cssClass: [W_50, H_50],
            attributes: {
                [REF_ATTRIBUTE]: "bottomRight"
            }
        });
        const bottomLeft = createHtmlElement("div", {
            cssClass: [W_50, H_50],
            attributes: {
                [REF_ATTRIBUTE]: "bottomLeft"
            }
        });

        cell.append(topLeft, topRight, bottomLeft, bottomRight);

        cell.subDivs = {
            topLeft,
            topRight,
            bottomLeft,
            bottomRight
        };
    }
}